/**
 * ============================================================================
 * [P6-04] TrackingPostCard - 트래킹 포스트 카드
 *
 * 기능:
 * - 포스트 제목/내용 표시
 * - 메트릭 표시 (조회수, 좋아요, 댓글, 공유, 팔로우)
 * - 액션 버튼 (편집, 삭제, 즐겨찾기, 활성/비활성)
 * ============================================================================
 */

import { useCallback, useMemo } from 'react'
import type { TrackingPost, TrackingMetric } from '@/types'
import styles from './Tracking.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TrackingPostCardProps {
  post: TrackingPost
  onEdit: (post: TrackingPost) => void
  onDelete: (id: string) => void
  onToggleFavorite: (id: string) => void
  onToggleActive: (id: string) => void
  onAddMetric: (postId: string) => void
  onManageMetrics?: (post: TrackingPost) => void  // [P5-01] 메트릭 관리 모달 열기
}

// ============================================================================
// 헬퍼: 최신 메트릭 가져오기
// ============================================================================
function getLatestMetric(metrics: TrackingMetric[]): TrackingMetric | null {
  if (!metrics || metrics.length === 0) return null
  return [...metrics].sort((a, b) => b.date.localeCompare(a.date))[0]
}

// ============================================================================
// 헬퍼: 숫자 포맷팅 (1000 -> 1K)
// ============================================================================
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

// ============================================================================
// 헬퍼: 날짜 포맷팅
// ============================================================================
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ============================================================================
// 플랫폼 아이콘
// ============================================================================
const PLATFORM_ICONS: Record<string, string> = {
  threads: '@',
  instagram: 'IG',
  twitter: 'X',
  facebook: 'FB',
  youtube: 'YT',
  tiktok: 'TT',
}

// ============================================================================
// [P6-04] TrackingPostCard Component
// ============================================================================
export function TrackingPostCard({
  post,
  onEdit,
  onDelete,
  onToggleFavorite,
  onToggleActive,
  onAddMetric,
  onManageMetrics,  // [P5-01] 메트릭 관리 모달 열기
}: TrackingPostCardProps) {
  // ========================================================================
  // 메트릭 계산
  // ========================================================================
  const latestMetric = useMemo(() => getLatestMetric(post.metrics), [post.metrics])

  const metrics = useMemo(() => ({
    views: latestMetric?.views ?? 0,
    likes: latestMetric?.likes ?? 0,
    comments: latestMetric?.comments ?? 0,
    shares: latestMetric?.shares ?? 0,
    follows: latestMetric?.follows ?? 0,
  }), [latestMetric])

  // ========================================================================
  // 핸들러
  // ========================================================================
  const handleEdit = useCallback(() => {
    onEdit(post)
  }, [post, onEdit])

  const handleDelete = useCallback(() => {
    if (window.confirm(`"${post.title}" 포스트를 삭제하시겠습니까?`)) {
      onDelete(post.id)
    }
  }, [post.id, post.title, onDelete])

  const handleToggleFavorite = useCallback(() => {
    onToggleFavorite(post.id)
  }, [post.id, onToggleFavorite])

  const handleToggleActive = useCallback(() => {
    onToggleActive(post.id)
  }, [post.id, onToggleActive])

  const handleAddMetric = useCallback(() => {
    onAddMetric(post.id)
  }, [post.id, onAddMetric])

  // [P5-01] 메트릭 관리 모달 열기 핸들러
  const handleManageMetrics = useCallback(() => {
    onManageMetrics?.(post)
  }, [post, onManageMetrics])

  const handleOpenUrl = useCallback(() => {
    if (post.url) {
      window.open(post.url, '_blank', 'noopener,noreferrer')
    }
  }, [post.url])

  // ========================================================================
  // Render
  // ========================================================================
  const isInactive = post.isActive === false
  const platformIcon = PLATFORM_ICONS[post.platform.toLowerCase()] ?? post.platform.charAt(0).toUpperCase()

  return (
    <div className={`${styles.postCard} ${isInactive ? styles.postCardInactive : ''}`}>
      {/* 헤더: 플랫폼, 제목, 즐겨찾기 */}
      <div className={styles.postHeader}>
        <span className={styles.platformBadge} title={post.platform}>
          {platformIcon}
        </span>
        <h3 className={styles.postTitle}>
          {post.title}
          {post.isFavorite && <span className={styles.favoriteIcon}>*</span>}
        </h3>
        <span className={styles.postDate}>{formatDate(post.updatedAt)}</span>
      </div>

      {/* 내용 (있는 경우) */}
      {post.content && (
        <p className={styles.postContent}>{post.content}</p>
      )}

      {/* 메트릭 */}
      <div className={styles.postMetrics}>
        <div className={styles.metricItem} title="조회수">
          <span className={styles.metricLabel}>Views</span>
          <span className={styles.metricNum}>{formatNumber(metrics.views)}</span>
        </div>
        <div className={styles.metricItem} title="좋아요">
          <span className={styles.metricLabel}>Likes</span>
          <span className={styles.metricNum}>{formatNumber(metrics.likes)}</span>
        </div>
        <div className={styles.metricItem} title="댓글">
          <span className={styles.metricLabel}>Comments</span>
          <span className={styles.metricNum}>{formatNumber(metrics.comments)}</span>
        </div>
        <div className={styles.metricItem} title="공유">
          <span className={styles.metricLabel}>Shares</span>
          <span className={styles.metricNum}>{formatNumber(metrics.shares)}</span>
        </div>
        {metrics.follows > 0 && (
          <div className={styles.metricItem} title="팔로우">
            <span className={styles.metricLabel}>Follows</span>
            <span className={styles.metricNum}>{formatNumber(metrics.follows)}</span>
          </div>
        )}
        <div className={styles.metricItem} title="기록수">
          <span className={styles.metricLabel}>Entries</span>
          <span className={styles.metricNum}>{post.metrics?.length ?? 0}</span>
        </div>
      </div>

      {/* 최신 메트릭 날짜 */}
      {latestMetric && (
        <div className={styles.latestMetricDate}>
          마지막 기록: {latestMetric.date}
        </div>
      )}

      {/* 액션 버튼 */}
      <div className={styles.postActions}>
        <button
          type="button"
          onClick={handleAddMetric}
          className={styles.actionBtn}
          title="메트릭 추가"
        >
          + 기록
        </button>
        {/* [P5-02] 기록 관리 버튼 추가 */}
        {onManageMetrics && (
          <button
            type="button"
            onClick={handleManageMetrics}
            className={styles.actionBtn}
            title="메트릭 관리"
          >
            관리
          </button>
        )}
        {post.url && (
          <button
            type="button"
            onClick={handleOpenUrl}
            className={styles.actionBtn}
            title="포스트 열기"
          >
            열기
          </button>
        )}
        <button
          type="button"
          onClick={handleEdit}
          className={styles.actionBtn}
          title="편집"
        >
          편집
        </button>
        <button
          type="button"
          onClick={handleToggleFavorite}
          className={`${styles.actionBtn} ${post.isFavorite ? styles.active : ''}`}
          title={post.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
        >
          {post.isFavorite ? '*' : '+'}
        </button>
        <button
          type="button"
          onClick={handleToggleActive}
          className={`${styles.actionBtn} ${isInactive ? styles.inactive : ''}`}
          title={isInactive ? '트래킹 활성화' : '트래킹 중지'}
        >
          {isInactive ? '비활성' : '활성'}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className={`${styles.actionBtn} ${styles.deleteBtn}`}
          title="삭제"
        >
          삭제
        </button>
      </div>
    </div>
  )
}

export default TrackingPostCard
